/**
 * Словарь ввода росчерком (06.09, владелец): загрузка /dict/ru-glide-v1.txt.
 *
 * Файл — windows-1251, одна словоформа (нижний регистр) на строку, номер
 * строки = ранг по частоте (0 — самое частое). Путь загрузки:
 *   1) IndexedDB "ub-glide" / store "dict" / key "v"+VERSION → ArrayBuffer;
 *   2) иначе fetch(GLIDE_DICT_URL) → arrayBuffer → в IndexedDB (ошибки IDB
 *      глотаем: приватный режим, квота, отсутствие API);
 *   3) декодирование TextDecoder("windows-1251"), фолбэк — ручная таблица cp1251;
 *   4) индекс Map<первая+последняя буква (ё→е), ранги[]>, чанками по setTimeout(0),
 *      чтобы не морозить UI (200 тыс. слов).
 * Промис мемоизирован на сессию; при ошибке сбрасывается — следующий вызов
 * попробует снова. Чистая часть (parseGlideDict/decodeCp1251) — без браузера,
 * покрыта dict.test.ts.
 */
import type { GlideDict } from "./types";

export const GLIDE_DICT_VERSION = 1;
export const GLIDE_DICT_URL = "/dict/ru-glide-v1.txt";

const IDB_NAME = "ub-glide";
const IDB_STORE = "dict";
const IDB_KEY = "v" + GLIDE_DICT_VERSION;
/** Слов на один синхронный шаг построения индекса (≈ единицы мс). */
const INDEX_CHUNK = 40_000;

/** Нормализация буквы под раскладку: на клавиатуре нет ё → е; прочее как есть. */
export function normGlideChar(ch: string): string {
  return ch === "ё" ? "е" : ch;
}

// ── cp1251 → юникод (фолбэк, если TextDecoder не знает windows-1251) ─────────
// 0x00–0x7F — ASCII; 0xC0–0xFF — А..я подряд (U+0410..U+044F); 0xA8/0xB8 — Ё/ё;
// остальной верхний диапазон (псевдографика, знаки) в словаре не встречается → U+FFFD.
let cp1251Table: Uint16Array | null = null;
function cp1251(): Uint16Array {
  if (cp1251Table) return cp1251Table;
  const t = new Uint16Array(256);
  for (let b = 0; b < 256; b++) {
    t[b] = b < 0x80 ? b : b >= 0xc0 ? 0x410 + (b - 0xc0) : b === 0xa8 ? 0x401 : b === 0xb8 ? 0x451 : 0xfffd;
  }
  cp1251Table = t;
  return t;
}

/** Ручное декодирование windows-1251 (кириллица + ASCII), кусками по 8 КиБ. */
export function decodeCp1251(bytes: Uint8Array): string {
  const t = cp1251();
  const parts: string[] = [];
  const step = 8192;
  const codes = new Array<number>(step);
  for (let i = 0; i < bytes.length; i += step) {
    const n = Math.min(step, bytes.length - i);
    for (let j = 0; j < n; j++) codes[j] = t[bytes[i + j] as number] as number;
    codes.length = n;
    parts.push(String.fromCharCode.apply(null, codes));
  }
  return parts.join("");
}

function decodeBuffer(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  if (typeof TextDecoder !== "undefined") {
    try {
      return new TextDecoder("windows-1251").decode(bytes);
    } catch {
      /* кодировка не поддержана — ручная таблица ниже */
    }
  }
  return decodeCp1251(bytes);
}

// ── разбор и индекс ──────────────────────────────────────────────────────────
function isLetter(code: number): boolean {
  return (code >= 0x430 && code <= 0x44f) || code === 0x451 || (code >= 0x61 && code <= 0x7a);
}

/**
 * Ключ индекса: первая + последняя БУКВА строки (дефисы/апострофы пропускаем —
 * для «кто-то» это «к»+«о»), обе через norm. Пусто, если букв нет.
 */
export function glideKeyOf(word: string, norm: (ch: string) => string = normGlideChar): string {
  let a = -1;
  let b = -1;
  for (let i = 0; i < word.length; i++) {
    if (isLetter(word.charCodeAt(i))) {
      if (a < 0) a = i;
      b = i;
    }
  }
  if (a < 0) return "";
  return norm(word[a] as string) + norm(word[b] as string);
}

/** Добавить в индекс слова с рангами [from, to). */
function indexRange(words: string[], index: Map<string, number[]>, from: number, to: number): void {
  for (let i = from; i < to; i++) {
    const key = glideKeyOf(words[i] as string);
    if (!key) continue;
    const arr = index.get(key);
    if (arr) arr.push(i);
    else index.set(key, [i]);
  }
}

/** Текст словаря → слова (без пустых строк и \r). */
function splitWords(text: string): string[] {
  const out: string[] = [];
  for (const raw of text.split("\n")) {
    const w = raw.endsWith("\r") ? raw.slice(0, -1) : raw;
    if (w) out.push(w);
  }
  return out;
}

/** Чистая функция: буфер windows-1251 → словарь целиком, синхронно. */
export function parseGlideDict(buf: ArrayBuffer): GlideDict {
  const words = splitWords(decodeBuffer(buf));
  const index = new Map<string, number[]>();
  indexRange(words, index, 0, words.length);
  return { words, index, norm: normGlideChar };
}

/** То же, но индекс строится чанками через setTimeout(0) — UI не блокируется. */
async function parseGlideDictChunked(buf: ArrayBuffer): Promise<GlideDict> {
  const words = splitWords(decodeBuffer(buf));
  const index = new Map<string, number[]>();
  for (let from = 0; from < words.length; from += INDEX_CHUNK) {
    indexRange(words, index, from, Math.min(from + INDEX_CHUNK, words.length));
    await new Promise<void>((r) => setTimeout(r, 0));
  }
  return { words, index, norm: normGlideChar };
}

// ── IndexedDB (все ошибки глотаются) ─────────────────────────────────────────
function idbOpen(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === "undefined") return resolve(null);
    try {
      const req = indexedDB.open(IDB_NAME, 1);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(IDB_STORE)) req.result.createObjectStore(IDB_STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

function idbGet(): Promise<ArrayBuffer | null> {
  return idbOpen().then(
    (db) =>
      new Promise((resolve) => {
        if (!db) return resolve(null);
        try {
          const req = db.transaction(IDB_STORE, "readonly").objectStore(IDB_STORE).get(IDB_KEY);
          req.onsuccess = () => {
            db.close();
            resolve(req.result instanceof ArrayBuffer ? req.result : null);
          };
          req.onerror = () => { db.close(); resolve(null); };
        } catch {
          db.close();
          resolve(null);
        }
      }),
  );
}

function idbPut(buf: ArrayBuffer): Promise<void> {
  return idbOpen().then(
    (db) =>
      new Promise((resolve) => {
        if (!db) return resolve();
        try {
          const tx = db.transaction(IDB_STORE, "readwrite");
          const store = tx.objectStore(IDB_STORE);
          store.clear(); // старые версии словаря не копим
          store.put(buf, IDB_KEY);
          tx.oncomplete = () => { db.close(); resolve(); };
          tx.onerror = () => { db.close(); resolve(); };
          tx.onabort = () => { db.close(); resolve(); };
        } catch {
          db.close();
          resolve();
        }
      }),
  );
}

// ── загрузка (мемоизированная) ───────────────────────────────────────────────
let dictPromise: Promise<GlideDict> | null = null;
let dictSync: GlideDict | null = null;

async function fetchDict(): Promise<ArrayBuffer> {
  const cached = await idbGet();
  if (cached) return cached;
  const res = await fetch(GLIDE_DICT_URL);
  if (!res.ok) throw new Error(`glide dict: HTTP ${res.status}`);
  const buf = await res.arrayBuffer();
  void idbPut(buf);
  return buf;
}

/** Загрузить словарь (один промис на сессию; при ошибке следующий вызов повторит). */
export function loadGlideDict(): Promise<GlideDict> {
  if (!dictPromise) {
    dictPromise = fetchDict()
      .then(parseGlideDictChunked)
      .then((d) => { dictSync = d; return d; })
      .catch((e: unknown) => { dictPromise = null; throw e; });
  }
  return dictPromise;
}

/** Прогрев в фоне без ожидания (хост зовёт при открытии клавиатуры). */
export function warmGlideDict(): void {
  loadGlideDict().catch(() => { /* сообщим при реальном обращении */ });
}

/** Уже загруженный словарь или null. */
export function getGlideDictSync(): GlideDict | null {
  return dictSync;
}
